import React, { useState, useEffect, useMemo } from 'react';
import {
  SxProps,
  TextField,
  TextFieldProps,
  IconButton,
  Box,
  Typography,
  InputLabel,
  Stack
} from '@mui/material';
import { CopyIcon } from 'components/Icons/CopyIcon';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Error } from '@mui/icons-material';

export type Props = TextFieldProps & {
  maxCharacters?: number;
  copyToClipboard?: boolean; // New prop
  isLoading?: boolean;
  loadingText?: string;
  withRemoveButton?: boolean;
  onRemove?: () => void;
  subLabel?: string;
  labelContainer?: SxProps;
  errorText?: string;
};

export const Input: React.FC<Props> = ({
  value,
  maxCharacters,
  label,
  copyToClipboard,
  withRemoveButton,
  onRemove,
  subLabel,
  labelContainer,
  error,
  errorText,
  ...rest
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copySuccess) {
      timer = setTimeout(() => {
        setCopySuccess(false);
      }, 2000); // Reset after 2 seconds
    }
    return () => {
      clearTimeout(timer);
    };
  }, [copySuccess]);

  const {
    helperText,
    helperTextStyle
  }: { helperText?: string; helperTextStyle?: SxProps } = maxCharacters
    ? {
        helperText: `${
          typeof value === 'string' ? value.length : 0
        }/${maxCharacters} characters`,
        helperTextStyle: {
          marginRight: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          color: 'customGrey.dark'
        }
      }
    : {};

  const endAdornment = useMemo(() => {
    if (copyToClipboard && typeof value === 'string') {
      return <CopyIcon copyValue={value} />;
    }

    return null;
  }, [copyToClipboard, value]);

  return (
    <Box width="100%">
      <Box
        sx={{
          mb: 1,
          ...labelContainer
        }}
      >
        {label && (
          <InputLabel
            sx={{ mb: 0, color: error ? 'error.main' : 'customGrey.dark' }}
          >
            {label}
          </InputLabel>
        )}
        {subLabel && (
          <Typography color="customGrey.dark" my="10px">
            {subLabel}
          </Typography>
        )}
        {error && errorText && (
          <Stack direction="row" gap="5px" sx={{ mt: 0.5 }}>
            <Error
              color="error"
              sx={{
                mt: '2px',
                fontSize: '16px'
              }}
            />
            <Typography
              color="error"
              variant="body2"
              fontSize="14px"
              fontWeight="bold"
            >
              {errorText}
            </Typography>
          </Stack>
        )}
      </Box>
      <Box display="flex" gap={1}>
        <TextField
          variant="outlined"
          fullWidth
          helperText={helperText}
          inputProps={{ maxLength: maxCharacters }}
          FormHelperTextProps={{ sx: helperTextStyle }}
          value={value}
          {...rest}
          error={error}
          InputProps={{
            endAdornment,
            ...rest.InputProps
          }}
        />
        {withRemoveButton && (
          <IconButton onClick={onRemove} color="primary">
            <HighlightOffIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
